import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";

const OTP_LENGTH = 6;
const EMPTY = Array.from({ length: OTP_LENGTH }, () => "");

const shake = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
  100% { transform: translateX(0); }
`;

const BoxRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  ${(props) =>
    props.$shaking &&
    css`
      animation: ${shake} 0.3s ease-in-out;
    `}
`;

const DigitBox = styled.input`
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 1px solid ${(props) => (props.hasError ? "#f96c62" : "#e2e8f0")};
  border-radius: 8px;
  box-sizing: border-box;
  text-align: center;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 19px;
  color: #0f172a;
  outline: none;

  &::placeholder {
    color: #475569;
  }

  &:focus {
    border-color: ${(props) => (props.hasError ? "#f96c62" : "#196fe0")};
  }
`;

export default function OtpDigitInput({ value, onChange, hasError, onSubmit, resetKey }) {
  const inputsRef = useRef([]);
  const [digits, setDigits] = useState(EMPTY);
  const [isShaking, setIsShaking] = useState(false);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // the parent bumps resetKey after a failed attempt or a resend; only a
  // failed attempt arrives with an error to show, so only that one shakes
  useEffect(() => {
    if (resetKey === undefined) return;
    setDigits(EMPTY);
    inputsRef.current[0]?.focus();
    if (hasError) setIsShaking(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const emit = (next) => {
    setDigits(next);
    onChange(next.every((digit) => digit) ? next.join("") : "");
  };

  const handleChange = (index, raw) => {
    const cleaned = (raw || "").replace(/\D/g, "");
    if (!cleaned) {
      const next = digits.slice();
      next[index] = "";
      emit(next);
      return;
    }

    if (cleaned.length >= OTP_LENGTH) {
      const filled = cleaned.slice(0, OTP_LENGTH).split("");
      emit(filled);
      inputsRef.current[OTP_LENGTH - 1]?.focus();
      return;
    }

    if (cleaned.length > 1) {
      const next = digits.slice();
      cleaned.split("").forEach((digit, offset) => {
        if (index + offset < OTP_LENGTH) next[index + offset] = digit;
      });
      emit(next);
      inputsRef.current[
        Math.min(index + cleaned.length, OTP_LENGTH - 1)
      ]?.focus();
      return;
    }

    const next = digits.slice();
    next[index] = cleaned.slice(-1);
    emit(next);
    if (index < OTP_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = digits.slice();
      if (next[index]) {
        next[index] = "";
        emit(next);
        return;
      }
      if (index > 0) {
        next[index - 1] = "";
        emit(next);
        inputsRef.current[index - 1]?.focus();
      }
      return;
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputsRef.current[index - 1]?.focus();
      return;
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      event.preventDefault();
      inputsRef.current[index + 1]?.focus();
      return;
    }
    if (event.key === "Enter" && value.length === OTP_LENGTH && onSubmit) {
      onSubmit();
    }
  };

  const handlePaste = (index, event) => {
    event.preventDefault();
    const pasted = (event.clipboardData.getData("text") || "").replace(
      /\D/g,
      ""
    );
    if (!pasted) return;
    const next = digits.slice();
    pasted.split("").forEach((digit, offset) => {
      if (index + offset < OTP_LENGTH) next[index + offset] = digit;
    });
    emit(next);
    inputsRef.current[
      Math.min(index + pasted.length, OTP_LENGTH - 1)
    ]?.focus();
  };

  return (
    <BoxRow $shaking={isShaking} onAnimationEnd={() => setIsShaking(false)}>
      {digits.map((digit, index) => (
        <DigitBox
          key={index}
          ref={(element) => {
            inputsRef.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={OTP_LENGTH}
          aria-label={`Digit ${index + 1}`}
          hasError={hasError}
          value={digit}
          onFocus={(event) => event.target.select()}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
        />
      ))}
    </BoxRow>
  );
}
